/**
 * LoopDetailsPanel — Autonomous loop cycle data for Pending post cards.
 * Authority: WI-LINKEDIN-LOOP-PHASE-5B
 *
 * Render gate: Array.isArray(post.cycle_history) && post.cycle_history.length > 1.
 * Returns null when the gate fails — no empty state, no zero state.
 *
 * Data fields sourced from data_province.json lines 818–822:
 *   cycle_history[].ranker_score, cycle_history[].ranker_fix_instruction
 *   (.weak_criterion, .suggested_fix, .keep_components)
 *   post.best_cycle_ranker_score
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { CaretDown } from '@phosphor-icons/react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import {
    DS_COLORS,
    DS_TYPOGRAPHY,
    DS_BORDERS,
    DS_SPACING,
    DS_MOTION,
    DESIGN_INTENTS,
} from '@/config/ui-invariants';

/* --------- MetricCell --------- */

function MetricCell({ children, value }) {
    return (
        <div
            className="rounded-lg border p-3"
            style={{
                borderColor: DS_BORDERS.color,
                background: DS_COLORS.surface.warm,
            }}
        >
            <div style={{ ...DS_TYPOGRAPHY.sectionLabel, color: DS_COLORS.text.secondary }}>
                {children}
            </div>
            <div
                style={{
                    fontFamily: DS_TYPOGRAPHY.bodyMd?.fontFamily,
                    fontSize: '18px',
                    fontWeight: 600,
                    color: DS_COLORS.text.primary,
                    marginTop: '4px',
                }}
            >
                {value}
            </div>
        </div>
    );
}

/* --------- LoopDetailsPanel --------- */

export default function LoopDetailsPanel({ post }) {
    const [isOpen, setIsOpen] = useState(false);

    // Render gate — locked per WI-LINKEDIN-LOOP-PHASE-5B spec.
    // Returns null when cycle_history is absent or has only one cycle.
    if (!Array.isArray(post.cycle_history) || post.cycle_history.length < 2) {
        return null;
    }

    // Data derivation — field names match data_province.json lines 818–822.
    const cycles = post.cycle_history;
    const cycleCount = cycles.length;
    const finalCycle = cycles[cycles.length - 1];

    const bestScore =
        typeof post.best_cycle_ranker_score === 'number'
            ? post.best_cycle_ranker_score
            : Math.max(
                  ...cycles.map((c) =>
                      Number.isFinite(c?.ranker_score) ? c.ranker_score : -Infinity
                  )
              );

    const finalScore = Number.isFinite(finalCycle?.ranker_score)
        ? finalCycle.ranker_score
        : null;

    // ranker_fix_instruction: {weak_criterion, suggested_fix, keep_components}
    const lastFix = finalCycle?.ranker_fix_instruction || null;

    // Motion values from DS_MOTION.collapsible
    const durationSec = (DS_MOTION.collapsible.durationMs || 240) / 1000;
    const ease = DS_MOTION.collapsible.ease || 'cubic-bezier(0.22, 1, 0.36, 1)';

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-4">
            <div
                style={{
                    border: `1px solid ${DS_BORDERS.color}`,
                    borderRadius: DS_BORDERS.radiusCard,
                    overflow: 'hidden',
                }}
            >
                {/* Trigger row */}
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        className="w-full flex items-center justify-between px-4 transition-colors"
                        style={{
                            minHeight: DS_SPACING.minTouchTarget + 'px',
                            background: DS_COLORS.surface.warm,
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <span
                                style={{
                                    ...DS_TYPOGRAPHY.sectionLabel,
                                    color: DS_COLORS.text.secondary,
                                }}
                            >
                                LOOP DETAILS
                            </span>
                            <span
                                className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${DESIGN_INTENTS.primary.bg} ${DESIGN_INTENTS.primary.text}`}
                            >
                                {cycleCount} cycles &middot; best{' '}
                                {Number.isFinite(bestScore) ? Math.round(bestScore) : '—'}
                            </span>
                        </div>
                        <CaretDown
                            weight="bold"
                            className="w-4 h-4 transition-transform duration-200 flex-shrink-0"
                            style={{
                                color: DS_COLORS.text.muted,
                                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    </button>
                </CollapsibleTrigger>

                <Separator />

                {/* Animated body */}
                <CollapsibleContent>
                    <motion.div
                        initial={{ opacity: 0, gridTemplateRows: '0fr' }}
                        animate={
                            isOpen
                                ? { opacity: 1, gridTemplateRows: '1fr' }
                                : { opacity: 0, gridTemplateRows: '0fr' }
                        }
                        transition={{ duration: durationSec, ease }}
                        className="px-4 py-3"
                    >
                        {/* Metric grid — 3 cells */}
                        <div className="grid grid-cols-3 gap-3">
                            <MetricCell value={cycleCount}>CYCLES</MetricCell>
                            <MetricCell
                                value={
                                    Number.isFinite(bestScore) ? Math.round(bestScore) : '—'
                                }
                            >
                                BEST SCORE
                            </MetricCell>
                            <MetricCell
                                value={
                                    finalScore !== null ? Math.round(finalScore) : '—'
                                }
                            >
                                FINAL SCORE
                            </MetricCell>
                        </div>

                        {/* Last cycle fix instruction */}
                        {lastFix && (
                            <div className="mt-4">
                                <div
                                    style={{
                                        ...DS_TYPOGRAPHY.sectionLabel,
                                        color: DS_COLORS.text.secondary,
                                    }}
                                >
                                    LAST CYCLE FIX
                                </div>
                                {lastFix.weak_criterion && (
                                    <div
                                        className="mt-1"
                                        style={{
                                            fontFamily: DS_TYPOGRAPHY.bodyMd?.fontFamily,
                                            fontSize: '14px',
                                            color: DS_COLORS.text.secondary,
                                        }}
                                    >
                                        {lastFix.weak_criterion}
                                    </div>
                                )}
                                {lastFix.suggested_fix && (
                                    <div
                                        className="mt-1 leading-relaxed"
                                        style={{
                                            fontFamily: DS_TYPOGRAPHY.bodyMd?.fontFamily,
                                            fontSize: '14px',
                                            color: DS_COLORS.text.primary,
                                        }}
                                    >
                                        {lastFix.suggested_fix}
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}
